/**
 * Keeps every media element slaved to the master clock (design §7.2).
 * Call tick() once per animation frame.
 *
 * @module
 */
import type { PresentedFrames } from "./presented";
import { DRIFT_IGNORE_MS, DRIFT_SEEK_MS, driftAction, type PlaybackClock, type TimeSource } from "./sync";

/** The subset of HTMLMediaElement the controller drives. */
export interface MediaLike {
  currentTime: number;
  playbackRate: number;
  readonly paused: boolean;
  readonly readyState: number;
  readonly seeking: boolean;
  readonly duration: number;
  readonly buffered: {
    readonly length: number;
    start(i: number): number;
    end(i: number): number;
  };
  play(): Promise<void>;
  pause(): void;
}

export interface Track {
  media: MediaLike;
  /** UTC of the element's time zero. */
  startUtcMs: number;
  /**
   * UTC intervals the element has recordings for, in order; a chain of files
   * with gaps between them. Default: from `startUtcMs` for the element's duration.
   */
  coverage?: readonly { fromUtcMs: number; toUtcMs: number }[];
  /**
   * The element feeds the AudioContext that drives the clock, so it cannot
   * drift: it is positioned on play and seek but never drift-corrected.
   * WebKit reports such an element's currentTime up to ~1 s ahead of what is
   * heard (spike 0.1a), which would otherwise trigger endless corrections.
   */
  clockLocked?: boolean;
  /** Not shown: kept paused, and never holds the clock. */
  suspended?: boolean;
  /** Experimental (tune in Phase 2, epic 2.1): frames actually shown; when given, small drifts are judged from these instead of currentTime. */
  frames?: PresentedFrames;
}

export interface ControllerOptions {
  /** "hold": stop the clock while any active stream is buffering. "continue": let it stall alone. */
  buffering?: "hold" | "continue";
  /**
   * "seek" (default): seek any element further out than `toleranceMs`. "rate": nudge
   * playbackRate for drifts under 500 ms; WebKitGTK ignores rate changes on
   * MSE playback (spike 0.1a). "none": free-run, for diagnostics.
   */
  correction?: "rate" | "seek" | "none";
  /** Per-stream drift left uncorrected (ms). Streams can sit at opposite ends, so the spread between them is up to twice this. */
  toleranceMs?: number;
  /** Wall clock in the same units as PresentedFrame.displayAt. */
  wall?: TimeSource;
}

const HAVE_CURRENT_DATA = 2;
const HAVE_FUTURE_DATA = 3;
/** Buffered data ahead of the position that counts as ready to play. */
const READY_AHEAD_S = 0.5;
/** How long before a track starts it is made to decode its first frame. */
const PREROLL_MS = 3000;
/**
 * After a seek, resume or correction, drift under DRIFT_SEEK_MS is left alone
 * for this long: a resumed element starts late (22-65 ms on the Android
 * tablet), and seeking it then makes it buffer, which holds the clock and
 * pauses everything again, in a loop.
 */
const SETTLE_MS = 500;
/** Drift under DRIFT_SEEK_MS is judged by its median over this long: single readings scatter ±25 ms on the tablet. */
const DRIFT_WINDOW_MS = 500;
/** After a seek, resume or correction, presented frames are ignored for this long... */
const PRESENTED_SETTLE_MS = 500;
/** ...and at least this many must have been shown before judging drift from them. */
const PRESENTED_MIN_FRAMES = 6;
/**
 * Wait between corrections made from presented frames. It doubles, up to the
 * maximum, while corrections don't bring a stream into tolerance: where a
 * frame lands after a seek can shift by about a frame, and chasing that
 * thrashes (headless Chromium, spike 0.1a).
 */
const PRESENTED_COOLDOWN_MS = 2000;
const PRESENTED_COOLDOWN_MAX_MS = 32_000;

export class PlaybackController {
  private wantPlaying = false;
  private held = false;
  private readonly prerolled = new Set<Track>();
  private readonly lastFix = new Map<Track, number>();
  private readonly drifts = new Map<Track, { at: number; ms: number }[]>();
  private readonly lastPresentedFix = new Map<Track, number>();
  private readonly presentedCooldown = new Map<Track, number>();
  /** Tracks whose last correction came from presented frames and hasn't been judged yet. */
  private readonly presentedUnjudged = new Set<Track>();
  private readonly wall: TimeSource;
  private readonly buffering: "hold" | "continue";
  private readonly correction: "rate" | "seek" | "none";
  private readonly toleranceMs: number;
  /** Corrections applied, for diagnostics. */
  readonly corrections = { nudges: 0, seeks: 0 };

  private readonly list: Track[];

  constructor(
    readonly clock: PlaybackClock,
    tracks: readonly Track[],
    options: ControllerOptions = {},
  ) {
    this.list = [...tracks];
    this.buffering = options.buffering ?? "hold";
    this.correction = options.correction ?? "seek";
    this.toleranceMs = options.toleranceMs ?? DRIFT_IGNORE_MS;
    this.wall = options.wall ?? (() => performance.now());
  }

  get tracks(): readonly Track[] {
    return this.list;
  }

  addTrack(t: Track): void {
    this.list.push(t);
    t.media.playbackRate = this.clock.playbackRate;
  }

  /** Swaps in `t` for `old`, which is no longer driven. */
  replaceTrack(old: Track, t: Track): void {
    const i = this.list.indexOf(old);
    if (i >= 0) this.list[i] = t;
    else this.list.push(t);
    this.prerolled.delete(old);
    this.presentedUnjudged.delete(old);
    this.lastFix.delete(old);
    this.drifts.delete(old);
    this.lastPresentedFix.delete(old);
    this.presentedCooldown.delete(old);
    t.media.playbackRate = this.clock.playbackRate;
  }

  /** Playing, or waiting for streams to buffer before playing. */
  get isPlaying(): boolean {
    return this.wantPlaying;
  }

  /** True while the clock is stopped waiting for a stream to buffer. */
  get isHeld(): boolean {
    return this.held;
  }

  play(): void {
    this.wantPlaying = true;
    this.tick();
  }

  pause(): void {
    this.wantPlaying = false;
    this.held = false;
    this.clock.pause();
    for (const t of this.tracks) t.media.pause();
  }

  /** Timeline speed; clock-locked tracks follow it here, the rest through drift correction. */
  setRate(rate: number): void {
    this.clock.setRate(rate);
    for (const t of this.tracks) t.media.playbackRate = rate;
  }

  seek(utcMs: number): void {
    this.prerolled.clear();
    this.markFixed(...this.tracks);
    this.clock.seek(utcMs);
    for (const t of this.tracks) {
      const pos = this.position(t);
      if (pos !== null) t.media.currentTime = pos;
    }
    this.tick();
  }

  tick(): void {
    this.updateClock();
    const playing = this.clock.isPlaying;
    for (const t of this.tracks) {
      const m = t.media;
      if (t.suspended) {
        if (!m.paused) m.pause();
        continue;
      }
      const pos = this.position(t);
      if (pos === null) {
        if (!m.paused) m.pause();
        const next = this.nextStart(t);
        if (next !== null && !m.seeking) {
          const at = (next - t.startUtcMs) / 1000;
          if (m.currentTime !== at || this.needsPreroll(t, next - this.clock.now())) {
            m.currentTime = at;
            this.prerolled.add(t);
          }
        }
        continue;
      }
      if (!playing) {
        if (!m.paused) m.pause();
        if (!m.seeking && Math.abs(m.currentTime - pos) * 1000 > this.toleranceMs) m.currentTime = pos;
        continue;
      }
      if (m.paused) {
        if (Math.abs(m.currentTime - pos) * 1000 > this.toleranceMs) m.currentTime = pos;
        m.play().catch(() => undefined);
        this.markFixed(t);
        continue;
      }
      if (m.seeking || t.clockLocked || this.correction === "none") continue;
      if (t.frames && this.correction === "seek" && Math.abs(m.currentTime - pos) * 1000 <= DRIFT_SEEK_MS) {
        const drift = this.presentedDriftMs(t, t.frames);
        if (drift === "waiting") continue;
        if (drift !== "dropping") {
          const off = Math.abs(drift) > this.toleranceMs;
          let cooldown = this.presentedCooldown.get(t) ?? PRESENTED_COOLDOWN_MS;
          if (this.presentedUnjudged.delete(t)) {
            cooldown = off ? Math.min(cooldown * 2, PRESENTED_COOLDOWN_MAX_MS) : PRESENTED_COOLDOWN_MS;
            this.presentedCooldown.set(t, cooldown);
          }
          const sinceLast = this.wall() - (this.lastPresentedFix.get(t) ?? -Infinity);
          if (off && sinceLast >= cooldown) {
            m.currentTime = Math.max(0, m.currentTime - drift / 1000);
            this.corrections.seeks++;
            this.markFixed(t);
            this.lastPresentedFix.set(t, this.wall());
            this.presentedUnjudged.add(t);
          }
          continue;
        }
      }
      let driftMs = (m.currentTime - pos) * 1000;
      if (Math.abs(driftMs) <= DRIFT_SEEK_MS) {
        const now = this.wall();
        const from = (this.lastFix.get(t) ?? -Infinity) + SETTLE_MS;
        if (now < from) continue;
        const recent = (this.drifts.get(t) ?? []).filter((d) => d.at >= Math.max(from, now - DRIFT_WINDOW_MS));
        recent.push({ at: now, ms: driftMs });
        this.drifts.set(t, recent);
        if (now - from < DRIFT_WINDOW_MS) continue;
        driftMs = median(recent.map((d) => d.ms));
      }
      const action = driftAction(pos, pos + driftMs / 1000, this.clock.playbackRate, this.toleranceMs);
      if (action.kind === "seek" || (action.kind === "nudge" && this.correction === "seek")) {
        m.currentTime = pos;
        this.corrections.seeks++;
        this.markFixed(t);
      } else if (m.playbackRate !== action.playbackRate) {
        m.playbackRate = action.playbackRate;
        if (action.kind === "nudge") this.corrections.nudges++;
        this.drifts.delete(t);
      }
    }
  }

  private updateClock(): void {
    if (!this.wantPlaying) return;
    const waiting =
      this.buffering === "hold" &&
      this.tracks.some((t) => {
        const pos = this.position(t);
        return pos !== null && !t.suspended && !this.atEnd(t) && !isReady(t.media, pos);
      });
    if (waiting) {
      if (this.clock.isPlaying) this.clock.pause();
      this.held = true;
    } else if (!this.clock.isPlaying) {
      this.clock.play();
      this.held = false;
      this.markFixed(...this.tracks);
    }
  }

  private markFixed(...tracks: Track[]): void {
    const now = this.wall();
    for (const t of tracks) this.lastFix.set(t, now);
  }

  /**
   * Median drift (ms, positive = ahead) of the frames shown since the last
   * seek, resume or correction, each judged at its display time against the
   * middle of the frame. A device that is dropping frames shows them late
   * because it is overloaded, and seeking would only make that worse, so
   * those streams fall back to currentTime (spike 0.1a, Android tablet).
   */
  private presentedDriftMs(t: Track, frames: PresentedFrames): number | "waiting" | "dropping" {
    const recent = frames.since((this.lastFix.get(t) ?? -Infinity) + PRESENTED_SETTLE_MS);
    const duration = frames.frameDuration();
    if (recent.length < PRESENTED_MIN_FRAMES || duration === null) return "waiting";
    if ((recent[recent.length - 1]?.dropped ?? 0) > (recent[0]?.dropped ?? 0)) return "dropping";
    const half = duration / 2;
    const now = this.wall();
    const clockNow = this.clock.now();
    const rate = this.clock.playbackRate;
    const drifts = recent
      .map((f) => (f.mediaTime + half - (clockNow + (f.displayAt - now) * rate - t.startUtcMs) / 1000) * 1000)
      .sort((a, b) => a - b);
    return drifts[Math.floor(drifts.length / 2)] ?? "waiting";
  }

  /**
   * WebKit keeps a parked element at HAVE_METADATA and takes about a second
   * to start it at a segment boundary; a seek makes it decode the first frame
   * in advance (spike 0.1a, real samples).
   */
  private needsPreroll(t: Track, untilStartMs: number): boolean {
    return untilStartMs < PREROLL_MS && t.media.readyState < HAVE_CURRENT_DATA && !this.prerolled.has(t);
  }

  /** Seconds into the track at the clock's position, or null outside its coverage. */
  private position(t: Track): number | null {
    const now = this.clock.now();
    const pos = (now - t.startUtcMs) / 1000;
    if (t.coverage) return t.coverage.some((c) => c.fromUtcMs <= now && now < c.toUtcMs) ? pos : null;
    const end = Number.isFinite(t.media.duration) ? t.media.duration : Infinity;
    return pos >= 0 && pos < end ? pos : null;
  }

  /**
   * In the last moments of a recording: its working copy can end a little
   * before the container said, so waiting for more data would hold forever.
   */
  private atEnd(t: Track): boolean {
    const now = this.clock.now();
    const end = t.coverage
      ? t.coverage.find((c) => c.fromUtcMs <= now && now < c.toUtcMs)?.toUtcMs
      : Number.isFinite(t.media.duration)
        ? t.startUtcMs + t.media.duration * 1000
        : undefined;
    return end !== undefined && end - now < END_SLACK_MS;
  }

  /** Where the track's next recording starts after the clock's position, if it has one. */
  private nextStart(t: Track): number | null {
    const now = this.clock.now();
    if (!t.coverage) return t.startUtcMs > now ? t.startUtcMs : null;
    return t.coverage.find((c) => c.fromUtcMs > now)?.fromUtcMs ?? null;
  }
}

/**
 * WebKit leaves a paused element at HAVE_CURRENT_DATA after a seek until it
 * plays, so readyState alone would hold the clock forever; check what is
 * actually buffered instead.
 */
/** How close to a recording's end a stream may run out of data without holding the clock. */
const END_SLACK_MS = 1000;

function isReady(m: MediaLike, pos: number): boolean {
  if (m.seeking || m.readyState < HAVE_CURRENT_DATA) return false;
  if (m.readyState >= HAVE_FUTURE_DATA) return true;
  const end = Number.isFinite(m.duration) ? m.duration : Infinity;
  for (let i = 0; i < m.buffered.length; i++) {
    if (m.buffered.start(i) <= pos && m.buffered.end(i) >= Math.min(pos + READY_AHEAD_S, end)) return true;
  }
  return false;
}

function median(xs: number[]): number {
  const sorted = [...xs].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)] ?? 0;
}
